import { useEffect, useRef, useState } from "react";
import { masterApi, apiError, formatPhone } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { protegeDoModalGlobal } from "@/lib/dialogModal";
import { inserirNoCursor } from "@/lib/indicacaoWeb";
import { smsInfo, SMS_LIMITE } from "@/lib/mensagensAuto";
import {
  SEGMENTOS, SEGMENTOS_NOVOS, ROTA_ETAPAS_CAMPANHA, CATEGORIAS, campoDias, variaveisDoSegmento, VARIAVEIS_DESCRICAO,
  conferirVariaveis, trocarSegmento, TEMPLATE_PADRAO, categoriasIniciais, rotuloCategorias, rotuloSegmento,
  payloadCampanha, resumoExecucao, motivosExclusao, resumoPreview, textoCorte, estimativaSms,
} from "@/lib/campanhas";
import CampanhaEnvios from "@/pages/Master/CampanhaEnvios";
import { Plus, Play, Pencil, Trash2, Eye, History, Save, ScrollText, AlertTriangle } from "lucide-react";

/**
 * Master > Cadastro > Campanhas. Fase 5 (pedido de 19/10, itens 5, 10 e 11): segmentos de prêmio de
 * troca de óleo ativo, códigos de indicação ativos e expirados e Rota de Fidelização (por etapa),
 * categorias de cliente, variáveis novas, preview com o total que realmente seria enviado e
 * histórico de envios com filtros de unidade, categoria, período e resultado (+ Excel).
 * Regras de tela em lib/campanhas.js.
 */

const FREQUENCIAS = { diaria: "Diária", semanal: "Semanal", mensal: "Mensal" };
const DIAS_SEMANA = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterCampanhas() {
  const { unidades = [] } = useUnit() || {};
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [waStatus, setWaStatus] = useState(null);   // WhatsApp em uso (para o preview dizer o que sai)
  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [enviosCampanha, setEnviosCampanha] = useState("");
  const enviosRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await masterApi.get("/master/campanhas");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const execNow = async (id, nome) => {
    const ok = await modal.confirm({
      title: "Executar campanha agora",
      message: `A campanha "${nome}" será disparada imediatamente para os clientes elegíveis.`,
      confirmText: "Executar agora",
    });
    if (!ok) return;
    try {
      const { data } = await masterApi.post(`/master/campanhas/${id}/executar-agora`, {});
      const r = resumoExecucao(data, nome);
      modal.success({ title: r.titulo, message: r.mensagem, details: r.details });
      load();
    } catch (e) { modal.error(apiError(e)); }
  };

  const toggleAtivo = async (c) => {
    try { await masterApi.put(`/master/campanhas/${c.id}`, { ativo: !c.ativo }); load(); }
    catch (e) { modal.error(apiError(e)); }
  };

  const del = async (id) => {
    const ok = await modal.confirm({
      title: "Excluir campanha",
      message: "Esta campanha será removida permanentemente. Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
      tone: "danger",
    });
    if (!ok) return;
    try { await masterApi.delete(`/master/campanhas/${id}`); modal.success("Campanha excluída com sucesso."); load(); }
    catch (e) { modal.error(apiError(e)); }
  };

  const preview = async (c) => {
    setPreviewing(c); setPreviewData(null); setWaStatus(null);
    // WhatsApp em uso agora (o mesmo status da tela do WhatsApp). Sem ele, valem os avisos que o
    // próprio preview traz; falha aqui nunca derruba o preview.
    Promise.resolve()
      .then(() => masterApi.get("/master/whatsapp/oficial/status"))
      .then((r) => setWaStatus(r?.data || null))
      .catch(() => setWaStatus(null));
    try { const { data } = await masterApi.get(`/master/campanhas/${c.id}/preview`); setPreviewData(data || {}); }
    catch (e) { modal.error(apiError(e)); setPreviewing(null); }
  };

  const loadHistory = async (c) => {
    setHistoryOf(c); setHistory([]);
    try { const { data } = await masterApi.get(`/master/campanhas/${c.id}/historico`); setHistory(Array.isArray(data) ? data : []); }
    catch (e) { modal.error(apiError(e)); setHistoryOf(null); }
  };

  // Do histórico de execuções para os envios da campanha (cliente por cliente), na própria tela.
  const verEnvios = (c) => {
    setHistoryOf(null);
    setEnviosCampanha(c.id);
    setTimeout(() => {
      try { enviosRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }); } catch (_) { /* ignora */ }
    }, 50);
  };

  const unidadeNome = (uid) => (uid === "all" || !uid ? "Todas" : (unidades.find((u) => u.id === uid)?.nome || "—"));
  const motivos = motivosExclusao(previewData);
  const amostra = Array.isArray(previewData?.amostra) ? previewData.amostra : [];
  const canalPreview = previewing?.canal || "sms";
  const resumo = resumoPreview(previewData, canalPreview, waStatus);
  const corte = textoCorte(previewData);

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-campanhas">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="chip mb-1.5 text-[10px] py-0.5">Recorrentes</div>
          <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Campanhas</h1>
        </div>
        <button data-testid="nova-campanha-btn" onClick={() => { setEditing(null); setOpen(true); }}
                className="btn-action btn-novo">
          <Plus size={13} /> Nova Campanha
        </button>
      </div>

      <div className="text-[11px] text-neutral-600 px-3 py-2.5 rounded-lg border border-dashed border-neutral-300 bg-white/60" data-testid="camp-ajuda">
        Rodam automaticamente no horário (fuso America/Sao_Paulo) pelo <b>canal escolhido na campanha (SMS ou WhatsApp)</b>.
        Variáveis de toda campanha: <code>{"{nome}"}</code>, <code>{"{desconto}"}</code>, <code>{"{telefone}"}</code>, <code>{"{unidade}"}</code>, <code>{"{valor}"}</code> (saldo de cashback).
        Por segmento: troca de óleo <code>{"{placa}"}</code> <code>{"{vencimento}"}</code> <code>{"{percentual}"}</code> <code>{"{dias_restantes}"}</code>;
        indicação <code>{"{qtd_codigos}"}</code> <code>{"{codigos}"}</code> <code>{"{qtd_expirados}"}</code> <code>{"{premio}"}</code>;
        Rota <code>{"{etapa}"}</code> <code>{"{proximo_desconto}"}</code> <code>{"{premio}"}</code>.
        No <b>SMS</b> a mensagem sai sem acentos e é cortada em <b>160 caracteres</b>. Quem recusou receber ofertas fica de fora.
        No WhatsApp oficial só sai o template aprovado, depois do aceite do cliente. Acompanhe no <b>Histórico de envios</b>.
      </div>

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Nome</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Segmento</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Frequência</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Categoria</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Última Exec.</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Envios</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ativo</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`campanha-row-${c.id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell className="text-xs" data-testid={`camp-segmento-${c.id}`}>{rotuloSegmento(c)}</TableCell>
                <TableCell className="text-xs">
                  {FREQUENCIAS[c.frequencia]}
                  {c.frequencia === "semanal" && ` · ${DIAS_SEMANA[c.dia_semana] || "?"}`}
                  {c.frequencia === "mensal" && ` · dia ${c.dia_mes}`}
                  {` · ${String(c.hora).padStart(2, "0")}:${String(c.minuto).padStart(2, "0")}`}
                </TableCell>
                <TableCell className="text-xs">{unidadeNome(c.unit_id)}</TableCell>
                <TableCell className="text-xs" data-testid={`camp-categorias-${c.id}`}>{rotuloCategorias(c.categorias_alvo)}</TableCell>
                <TableCell className="text-xs">{c.ultima_execucao ? new Date(c.ultima_execucao).toLocaleString("pt-BR") : "—"}</TableCell>
                <TableCell>{c.total_envios || 0}</TableCell>
                <TableCell><Switch data-testid={`camp-switch-${c.id}`} checked={c.ativo} onCheckedChange={() => toggleAtivo(c)} /></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button data-testid={`camp-exec-${c.id}`} onClick={() => execNow(c.id, c.nome)}
                            title="Executar agora" className="icon-action icon-action--green"><Play size={14} /></button>
                    <button data-testid={`camp-preview-${c.id}`} onClick={() => preview(c)}
                            title="Preview" className="icon-action"><Eye size={14} /></button>
                    <button data-testid={`camp-hist-${c.id}`} onClick={() => loadHistory(c)}
                            title="Histórico" className="icon-action"><History size={14} /></button>
                    <button data-testid={`camp-edit-${c.id}`} onClick={() => { setEditing(c); setOpen(true); }}
                            title="Editar" className="icon-action icon-action--edit"><Pencil size={14} /></button>
                    <button data-testid={`camp-del-${c.id}`} onClick={() => del(c.id)}
                            title="Excluir" className="icon-action icon-action--danger"><Trash2 size={14} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-neutral-500 py-6">Nenhuma campanha.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <CampanhaEnvios ref={enviosRef} campanhas={items} campanhaId={enviosCampanha} onCampanha={setEnviosCampanha} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl border-none p-0 max-h-[90vh] overflow-auto" {...protegeDoModalGlobal}>
          <div className="bg-white p-5 rounded-2xl">
            {open && <CampanhaForm unidades={unidades} campanha={editing} onDone={() => { setOpen(false); load(); }} />}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewing} onOpenChange={(v) => !v && setPreviewing(null)}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0 max-h-[90vh] overflow-auto">
          <div className="bg-white p-5 rounded-2xl">
            <DialogHeader><DialogTitle className="font-serif-display text-xl pr-6">Preview · {previewing?.nome}</DialogTitle></DialogHeader>
            {previewData ? (
              <div className="mt-2 space-y-2" data-testid="camp-preview">
                <div>
                  <div className={`font-serif-display text-4xl ${resumo.nadaSai ? "text-neutral-400" : ""}`} data-testid="camp-preview-total">{resumo.total}</div>
                  <div className={`text-xs ${resumo.nadaSai ? "text-rose-700 font-semibold" : "text-neutral-500"}`} data-testid="camp-preview-legenda">{resumo.legenda}</div>
                </div>
                {resumo.avisos.length > 0 && (
                  <div className="space-y-1.5" data-testid="camp-preview-avisos">
                    {resumo.avisos.map((a, i) => (
                      <div key={a} className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900"
                           data-testid={`camp-preview-aviso-${i}`}>
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                        <span>{a}</span>
                      </div>
                    ))}
                  </div>
                )}
                {motivos.length > 0 && (
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2" data-testid="camp-preview-motivos">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Ficam de fora</div>
                    <ul className="text-[11.5px] space-y-0.5">
                      {motivos.map((m) => <li key={m.chave} data-testid={`camp-preview-motivo-${m.chave}`}>{m.rotulo}: <b>{m.n}</b></li>)}
                    </ul>
                  </div>
                )}
                {corte && (
                  <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900" data-testid="camp-preview-cortado">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                    {corte}
                  </div>
                )}
                {amostra.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Amostra</div>
                    <ul className="text-xs space-y-1">
                      {amostra.map((c, i) => (
                        <li key={i}>
                          <div>{c.nome} — {formatPhone(c.telefone)}</div>
                          {c.mensagem && (
                            <div className="text-[11px] text-neutral-500 whitespace-pre-wrap break-words" data-testid={`camp-preview-msg-${i}`}>
                              {canalPreview === "ambos" && <span className="font-semibold text-neutral-600">WhatsApp: </span>}
                              {c.mensagem}
                            </div>
                          )}
                          {canalPreview === "ambos" && c.mensagem_sms && (
                            <div className="text-[11px] text-neutral-500 whitespace-pre-wrap break-words" data-testid={`camp-preview-sms-${i}`}>
                              <span className="font-semibold text-neutral-600">SMS (como sai): </span>{c.mensagem_sms}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : <div className="text-sm">Calculando...</div>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="max-w-2xl rounded-2xl border-none p-0">
          <div className="bg-white p-5 rounded-2xl">
            <DialogHeader><DialogTitle className="font-serif-display text-xl pr-6">Histórico · {historyOf?.nome}</DialogTitle></DialogHeader>
            {/* A janela já rola (altura máxima da tela): a tabela não ganha rolagem própria. */}
            <Table maxHeight={false}>
              <TableHeader><TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Quando</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Segmento</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Enviados</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Erros</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{new Date(h.executado_em).toLocaleString("pt-BR")}</TableCell>
                    <TableCell className="text-xs">{h.tipo}</TableCell>
                    <TableCell>{h.total_segmento}</TableCell>
                    <TableCell className="font-semibold text-rose-600">{h.enviados}</TableCell>
                    <TableCell>{h.erros}</TableCell>
                  </TableRow>
                ))}
                {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-6">Nenhuma execução ainda.</TableCell></TableRow>}
              </TableBody>
            </Table>
            {historyOf && (
              <button type="button" data-testid="camp-hist-ver-envios" onClick={() => verEnvios(historyOf)}
                      className="btn-ghost h-9 px-3 mt-3 text-[12px] font-medium inline-flex items-center gap-1.5" style={{ borderRadius: 8 }}>
                <ScrollText size={13} /> Ver os envios desta campanha (cliente por cliente)
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampanhaForm({ unidades, campanha, onDone }) {
  const [f, setF] = useState(() => ({
    nome: campanha?.nome || "",
    segmento: campanha?.segmento || "sumidos",
    dias_segmento: campanha?.dias_segmento ?? (campoDias(campanha?.segmento)?.padrao || 30),
    min_abastecimentos: campanha?.min_abastecimentos ?? 4,
    mensagem_template: campanha?.mensagem_template || TEMPLATE_PADRAO,
    aplicar_cupom_saudade: campanha?.aplicar_cupom_saudade ?? true,
    desconto_centavos_litro: campanha?.desconto_centavos_litro ?? 15,
    frequencia: campanha?.frequencia || "semanal",
    hora: campanha?.hora ?? 10,
    minuto: campanha?.minuto ?? 0,
    dia_semana: campanha?.dia_semana ?? 0,
    dia_mes: campanha?.dia_mes ?? 1,
    unit_id: campanha?.unit_id || "all",
    ativo: campanha?.ativo ?? true,
    incluir_saldo_cashback: campanha?.incluir_saldo_cashback ?? true,
    canal: campanha?.canal || "sms",
    categorias_alvo: categoriasIniciais(campanha),
    rota_etapa: campanha?.rota_etapa || "",
  }));
  const [salvando, setSalvando] = useState(false);
  const msgRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const dias = campoDias(f.segmento);
  const variaveis = variaveisDoSegmento(f.segmento);
  const { erro: erroVars, aviso: avisoVars } = conferirVariaveis(f.mensagem_template, f.segmento);
  const semCategoria = !f.categorias_alvo.length;
  const novo = SEGMENTOS_NOVOS.includes(f.segmento);

  const alternarCategoria = (k) => setF((p) => ({
    ...p,
    categorias_alvo: p.categorias_alvo.includes(k) ? p.categorias_alvo.filter((x) => x !== k) : [...p.categorias_alvo, k],
  }));

  const inserirVariavel = (v) => {
    const el = msgRef.current;
    const r = inserirNoCursor(f.mensagem_template, `{${v}}`, el?.selectionStart, el?.selectionEnd);
    set("mensagem_template", r.texto);
    setTimeout(() => {
      try { if (el) { el.focus(); el.setSelectionRange(r.cursor, r.cursor); } } catch (_) { /* ignora */ }
    }, 0);
  };

  const submit = async () => {
    if (!f.nome.trim()) return modal.error("Nome obrigatório");
    if (!f.mensagem_template.trim()) return modal.error("Mensagem obrigatória");
    if (erroVars) return modal.error(erroVars);
    if (semCategoria) return modal.error("Escolha ao menos uma categoria de cliente.");
    setSalvando(true);
    try {
      const body = payloadCampanha(f);
      if (campanha) await masterApi.put(`/master/campanhas/${campanha.id}`, body);
      else await masterApi.post("/master/campanhas", body);
      modal.success(campanha ? "Campanha atualizada" : "Campanha criada");
      onDone();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSalvando(false); }
  };

  const sms = (f.canal === "sms" || f.canal === "ambos") ? smsInfo(f.mensagem_template) : null;
  const estimativa = sms ? estimativaSms(f.mensagem_template) : { aviso: null };

  return (
    <div className="space-y-3" data-testid="camp-form">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-xl pr-6">{campanha ? "Editar Campanha" : "Nova Campanha"}</DialogTitle>
      </DialogHeader>
      <Field label="Nome"><input data-testid="camp-nome" value={f.nome} onChange={(e) => set("nome", e.target.value)} className={inputCls} /></Field>

      <Field label="Segmento">
        <select data-testid="camp-segmento" value={f.segmento} onChange={(e) => { const v = e.target.value; setF((p) => trocarSegmento(p, v)); }} className={inputCls}>
          {Object.entries(SEGMENTOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>

      {dias && (
        <Field label={dias.label}>
          <input type="number" min={1} max={365} data-testid="camp-dias" value={f.dias_segmento}
                 onChange={(e) => set("dias_segmento", parseInt(e.target.value) || dias.padrao)} className={inputCls} />
          {dias.ajuda && <p className="text-[10.5px] text-neutral-500 mt-1" data-testid="camp-dias-ajuda">{dias.ajuda}</p>}
        </Field>
      )}
      {(f.segmento === "frequentes" || f.segmento === "low_freq") && (
        <Field label="Limite de abastecimentos">
          <input type="number" data-testid="camp-minab" value={f.min_abastecimentos} onChange={(e) => set("min_abastecimentos", parseInt(e.target.value) || 4)} className={inputCls} />
        </Field>
      )}
      {f.segmento === "rota_fidelizacao" && (
        <Field label="Etapa da Rota">
          <select data-testid="camp-rota-etapa" value={f.rota_etapa} onChange={(e) => set("rota_etapa", e.target.value)} className={inputCls}>
            {ROTA_ETAPAS_CAMPANHA.map(([k, lbl]) => <option key={k || "todas"} value={k}>{lbl}</option>)}
          </select>
          <p className="text-[10.5px] text-neutral-500 mt-1" data-testid="camp-rota-ajuda">
            Sem limite por cliente: cada execução envia para todos da etapa escolhida. Prefira frequência semanal ou mensal.
          </p>
        </Field>
      )}

      <Field label="Categorias de cliente">
        <div className="flex flex-wrap gap-1.5" data-testid="camp-categorias">
          {CATEGORIAS.map(([k, lbl]) => {
            const on = f.categorias_alvo.includes(k);
            return (
              <button key={k} type="button" data-testid={`camp-cat-${k}`} aria-pressed={on} onClick={() => alternarCategoria(k)}
                      className={`h-9 px-3 text-[12px] font-medium ${on ? "btn-primary-blue" : "btn-ghost"}`}
                      style={{ borderRadius: 8 }}>
                {lbl}
              </button>
            );
          })}
        </div>
        {semCategoria && <p className="text-[11px] text-rose-700 mt-1" data-testid="camp-cat-erro">Escolha ao menos uma categoria.</p>}
      </Field>

      <Field label="Canal de envio">
        <select data-testid="camp-canal" value={f.canal} onChange={(e) => set("canal", e.target.value)} className={inputCls}>
          <option value="sms">SMS</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="ambos">SMS + WhatsApp</option>
        </select>
        {f.canal !== "sms" && (
          <p className="text-[10.5px] text-amber-800 mt-1" data-testid="camp-canal-aviso">
            {f.canal === "whatsapp"
              ? "Com a API oficial da Meta em uso, a campanha por WhatsApp sai com o texto padrão aprovado das promoções, só depois da aprovação da Meta e da liberação em Mensagens automáticas > Envio das mensagens: só WhatsApp, até lá nada é enviado. Confira no Preview."
              : "Com a API oficial da Meta em uso, o WhatsApp da campanha sai com o texto padrão aprovado das promoções, só depois da aprovação da Meta e da liberação em Mensagens automáticas > Envio das mensagens; até lá sai só o SMS. Confira no Preview."}
          </p>
        )}
      </Field>

      <Field label="Mensagem">
        <textarea ref={msgRef} data-testid="camp-msg" value={f.mensagem_template} onChange={(e) => set("mensagem_template", e.target.value)}
                  rows={4}
                  className={`w-full rounded-lg bg-white border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/15 transition ${erroVars || !f.mensagem_template.trim() ? "border-rose-400" : "border-neutral-200 focus:border-[color:var(--brand-blue)]"}`} />
        <div className="flex flex-wrap items-center gap-1 mt-1" data-testid="camp-variaveis">
          <span className="text-[10.5px] text-neutral-500 mr-1">Variáveis:</span>
          {variaveis.map((v) => (
            <button key={v} type="button" data-testid={`camp-var-${v}`} title={VARIAVEIS_DESCRICAO[v]} onClick={() => inserirVariavel(v)}
                    className="px-1.5 h-6 rounded-md border border-neutral-200 bg-white text-[10.5px] font-mono hover:bg-neutral-50">
              {`{${v}}`}
            </button>
          ))}
        </div>
        <ul className="mt-1 text-[10.5px] text-neutral-500 grid sm:grid-cols-2 gap-x-3" data-testid="camp-variaveis-ajuda">
          {variaveis.map((v) => <li key={v}><span className="font-mono">{`{${v}}`}</span>: {VARIAVEIS_DESCRICAO[v]}</li>)}
        </ul>
        {erroVars && <p className="text-[11.5px] text-rose-700 mt-1" data-testid="camp-msg-erro">{erroVars}</p>}
        {avisoVars && <p className="text-[11px] text-amber-800 mt-1" data-testid="camp-msg-aviso">{avisoVars}</p>}
        {sms && (
          <div data-testid="camp-msg-sms-len"
               className={`text-[10px] mt-1 ${sms.passou ? "text-rose-600 font-semibold" : "text-neutral-500"}`}>
            SMS: {sms.caracteres}/{SMS_LIMITE} caracteres (sem acentos){sms.passou ? " — será CORTADA no envio!" : ""}
            {"  "}· variáveis podem aumentar o tamanho final
          </div>
        )}
        {sms && estimativa.aviso && (
          <p className="text-[11px] text-amber-800 mt-1" data-testid="camp-msg-sms-estimativa">{estimativa.aviso}</p>
        )}
      </Field>

      <Toggle label="Aplicar cupom saudade"
              hint={novo ? "Marca cupom saudade para o próximo abastecimento. Neste segmento normalmente fica desligado." : "Marca cliente com cupom ativo para próximo abastecimento"}
              checked={f.aplicar_cupom_saudade} onChange={(v) => set("aplicar_cupom_saudade", v)} testid="camp-saudade" />

      <Toggle label="Anexar saldo de cashback" hint="Adiciona o saldo de cashback do cliente à mensagem (boost de retorno)"
              checked={f.incluir_saldo_cashback} onChange={(v) => set("incluir_saldo_cashback", v)} testid="camp-cashback-suffix" />

      {f.aplicar_cupom_saudade && (
        <Field label="Desconto (¢/L)">
          <input type="number" step="0.5" data-testid="camp-desc" value={f.desconto_centavos_litro}
                 onChange={(e) => set("desconto_centavos_litro", parseFloat(e.target.value) || 0)} className={inputCls} />
        </Field>
      )}

      <Field label="Frequência">
        <select data-testid="camp-freq" value={f.frequencia} onChange={(e) => set("frequencia", e.target.value)} className={inputCls}>
          {Object.entries(FREQUENCIAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>

      {f.frequencia === "semanal" && (
        <Field label="Dia da semana">
          <select data-testid="camp-dow" value={String(f.dia_semana)} onChange={(e) => set("dia_semana", parseInt(e.target.value))} className={inputCls}>
            {DIAS_SEMANA.map((d, i) => <option key={i} value={String(i)}>{d}</option>)}
          </select>
        </Field>
      )}
      {f.frequencia === "mensal" && (
        <Field label="Dia do mês (1-28)">
          <input type="number" min={1} max={28} data-testid="camp-dom" value={f.dia_mes}
                 onChange={(e) => set("dia_mes", parseInt(e.target.value) || 1)} className={inputCls} />
        </Field>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Field label="Hora">
          <input type="number" min={0} max={23} data-testid="camp-hora" value={f.hora}
                 onChange={(e) => set("hora", parseInt(e.target.value) || 0)} className={inputCls} />
        </Field>
        <Field label="Minuto">
          <input type="number" min={0} max={59} data-testid="camp-min" value={f.minuto}
                 onChange={(e) => set("minuto", parseInt(e.target.value) || 0)} className={inputCls} />
        </Field>
      </div>

      <Field label={f.segmento === "oleo_premio_ativo" ? "Unidade (onde o prêmio foi ganho)" : "Unidade"}>
        <select data-testid="camp-unit" value={f.unit_id} onChange={(e) => set("unit_id", e.target.value)} className={inputCls}>
          <option value="all">Todas</option>
          {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
      </Field>

      <Toggle label="Ativa" checked={f.ativo} onChange={(v) => set("ativo", v)} testid="camp-ativo" />

      <button data-testid="camp-submit" onClick={submit} disabled={salvando}
              className="btn-action btn-salvar w-full">
        <Save size={15} /> {salvando ? "Salvando..." : (campanha ? "Salvar" : "Criar Campanha")}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white p-2.5">
      <div>
        <div className="text-[12px] font-medium">{label}</div>
        {hint && <div className="text-[10px] text-neutral-500 mt-0.5">{hint}</div>}
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
